import { Fragment } from 'preact';
import { render } from 'preact-render-to-string';
import {
  BasicEmailWrapper,
  Heading,
  Italic,
  Line,
  Mailto,
  MarketingFooter,
  PrimaryAction,
  SupportFooter,
} from './shared';
import { Currency } from 'server/types';
import { TaxRegistration } from 'server/payments/tax-registrations';
import dayjs from 'dayjs';
import * as fmt from 'shared/payments/fmt';

export interface Payment {
  currency: Currency;
  amount: number;
  date: Date | string;
  taxAmount?: number;
}

export interface PaymentReceiptProps extends PaymentEmailParams {
  payment: Payment;
  isAccessDisabled: boolean;
  taxRegistrations?: TaxRegistration[];
}

export interface PaymentEmailParams {
  title: string;
  description?: string;
  cardBrand?: string;
  cardLast4?: string;
  companyName?: string;
  buyerName?: string;
  billingAddress?: string;
  manageUrl: string;
  timezone: string;
  from: string;
  fromName?: string;
  replyTo?: string;
  to: string;
  domain: string;
  mailgunDomain?: string;
}

function paymentDate(date: Payment['date'], timezone: string) {
  return dayjs(date).tz(timezone).format('MMMM D, YYYY');
}

/**
 * Notification email for payment plan cancellation
 */
export function PaymentPlanCanceled(opts: PaymentReceiptProps) {
  const firstName = opts.buyerName?.split(' ')[0];
  const greeting = firstName ? `Hi ${firstName},` : 'Hi there,';

  return render(
    <BasicEmailWrapper footer={<SupportFooter email={opts.replyTo} />}>
      <Heading>We're sorry to see you go</Heading>
      <Line>{greeting}</Line>
      <Line>
        We've processed your cancellation for <strong>{opts.title}</strong>.
      </Line>
      {!opts.isAccessDisabled && (
        <Line>
          You'll continue to have full access to everything — live courses, trainings, community,
          and support — until the end of your current billing period. We want to make sure you get
          the most out of your remaining time with us.
        </Line>
      )}
      {opts.isAccessDisabled && (
        <Line>
          Your membership access for <Italic>{opts.title}</Italic> has been updated. If you believe
          this was a mistake, please don't hesitate to reach out.
        </Line>
      )}
      <Line>
        If you ever want to come back, you're always welcome. And if there's anything we can do to
        help in the meantime, just reply to this email — we'd love to hear from you.
      </Line>
      <Line>Wishing you all the best,</Line>
      <Line style="margin-bottom:0">The Mirasee Team</Line>
    </BasicEmailWrapper>,
  );
}

/**
 * Payment receipt email
 */
export function PaymentReceipt(opts: PaymentReceiptProps) {
  return render(
    <BasicEmailWrapper footer={<SupportFooter email={opts.replyTo} />}>
      <Heading>Thank you!</Heading>
      <Line>
        Your payment for <em>{opts.title}</em> has been processed.
      </Line>
      <PaymentTable opts={opts} totalLabel="Payment Total" />
    </BasicEmailWrapper>,
  );
}

/**
 * Payment refund receipt email
 */
export function PaymentRefundReceipt(opts: PaymentReceiptProps) {
  return render(
    <BasicEmailWrapper footer={<SupportFooter email={opts.replyTo} />}>
      <Heading>Refund processed</Heading>
      <Line>
        Your refund for <em>{opts.title}</em> has been processed.
      </Line>
      <PaymentTable opts={opts} totalLabel="Refund Total" />
    </BasicEmailWrapper>,
  );
}

/**
 * User notification for payment failure
 */
export function PaymentFailure(opts: PaymentReceiptProps) {
  return render(
    <BasicEmailWrapper footer={<SupportFooter email={opts.replyTo} />}>
      <Heading>Your payment failed</Heading>
      <Line>
        You may need to update your payment method for <em>{opts.title}</em>.
      </Line>
      <PaymentTable opts={opts} totalLabel="Attempted Amount" highlight="#B91C1C" />
      <PrimaryAction href={opts.manageUrl}>Manage Your Payments</PrimaryAction>
      <Line>
        If you have further questions, please email {opts.fromName} at <Mailto email={opts.from} />
      </Line>
    </BasicEmailWrapper>,
  );
}

/**
 * Table view of payments for payment emails
 */
function PaymentTable({
  opts,
  totalLabel,
  highlight,
}: {
  opts: PaymentReceiptProps;
  totalLabel: string;
  highlight?: string;
}) {
  const amount = fmt.price({
    currency: opts.payment.currency,
    priceInCents: opts.payment.amount,
  });
  const cardLabel =
    opts.cardBrand && opts.cardLast4
      ? `${opts.cardBrand.charAt(0).toUpperCase() + opts.cardBrand.slice(1)} ending in ${opts.cardLast4}`
      : undefined;
  return (
    <table
      cellPadding="0"
      cellSpacing="0"
      style="width:100%;margin:1.5em 0;border-collapse:collapse;"
    >
      {opts.buyerName && (
        <>
          <tr>
            <td style="padding:0.75em 0;color:#667;font-size:0.9em;">Name</td>
            <td style="padding:0.75em 0;text-align:right;">{opts.buyerName}</td>
          </tr>
          <tr>
            <td colSpan={2} style="padding:0;">
              <hr style="border:0;border-top:1px solid #e2e8f0;margin:0;" />
            </td>
          </tr>
        </>
      )}
      {opts.companyName && (
        <>
          <tr>
            <td style="padding:0.75em 0;color:#667;font-size:0.9em;">Company</td>
            <td style="padding:0.75em 0;text-align:right;">{opts.companyName}</td>
          </tr>
          <tr>
            <td colSpan={2} style="padding:0;">
              <hr style="border:0;border-top:1px solid #e2e8f0;margin:0;" />
            </td>
          </tr>
        </>
      )}
      {opts.billingAddress && (
        <>
          <tr>
            <td style="padding:0.75em 0;color:#667;font-size:0.9em;">Address</td>
            <td
              style="padding:0.75em 0;text-align:right;"
              dangerouslySetInnerHTML={{ __html: opts.billingAddress }}
            />
          </tr>
          <tr>
            <td colSpan={2} style="padding:0;">
              <hr style="border:0;border-top:1px solid #e2e8f0;margin:0;" />
            </td>
          </tr>
        </>
      )}
      <tr>
        <td style="padding:0.75em 0;color:#667;font-size:0.9em;">Date</td>
        <td style="padding:0.75em 0;text-align:right;">
          {paymentDate(opts.payment.date, opts.timezone)}
        </td>
      </tr>
      <tr>
        <td colSpan={2} style="padding:0;">
          <hr style="border:0;border-top:1px solid #e2e8f0;margin:0;" />
        </td>
      </tr>
      {cardLabel && (
        <>
          <tr>
            <td style="padding:0.75em 0;color:#667;font-size:0.9em;">Payment method</td>
            <td style="padding:0.75em 0;text-align:right;">{cardLabel}</td>
          </tr>
          <tr>
            <td colSpan={2} style="padding:0;">
              <hr style="border:0;border-top:1px solid #e2e8f0;margin:0;" />
            </td>
          </tr>
        </>
      )}
      {!!opts.payment.taxAmount && (
        <>
          <tr>
            <td style="padding:0.75em 0;color:#667;font-size:0.9em;">Subtotal</td>
            <td style="padding:0.75em 0;text-align:right;">
              {fmt.price({
                currency: opts.payment.currency,
                priceInCents: opts.payment.amount - opts.payment.taxAmount,
              })}
            </td>
          </tr>
          <tr>
            <td colSpan={2} style="padding:0;">
              <hr style="border:0;border-top:1px solid #e2e8f0;margin:0;" />
            </td>
          </tr>
          <tr>
            <td style="padding:0.75em 0;color:#667;font-size:0.9em;">Tax</td>
            <td style="padding:0.75em 0;text-align:right;">
              {fmt.price({
                currency: opts.payment.currency,
                priceInCents: opts.payment.taxAmount,
              })}
            </td>
          </tr>
          <tr>
            <td colSpan={2} style="padding:0;">
              <hr style="border:0;border-top:1px solid #e2e8f0;margin:0;" />
            </td>
          </tr>
          {opts.taxRegistrations?.map((registration) => (
            <Fragment key={registration.label}>
              <tr>
                <td style="padding:0.75em 0;color:#667;font-size:0.9em;">
                  {registration.label} registration
                </td>
                <td style="padding:0.75em 0;text-align:right;">{registration.number}</td>
              </tr>
              <tr>
                <td colSpan={2} style="padding:0;">
                  <hr style="border:0;border-top:1px solid #e2e8f0;margin:0;" />
                </td>
              </tr>
            </Fragment>
          ))}
        </>
      )}
      <tr>
        <td style="padding:0.75em 0;color:#667;font-size:0.9em;">{totalLabel}</td>
        <td
          style={`padding:0.75em 0;text-align:right;font-size:1.25em;font-weight:700;color:${highlight || '#003DA6'};`}
        >
          {amount}
        </td>
      </tr>
    </table>
  );
}

/**
 * Guide notification email for student payment plan cancellation
 */
export function StudentPlanCanceled({
  title,
  studentName,
  studentEmail,
  manageUrl,
  isAccessDisabled,
}: PaymentEmailParams & { studentName: string; studentEmail: string; isAccessDisabled: boolean }) {
  return render(
    <BasicEmailWrapper footer={<MarketingFooter />}>
      <h2>{title}</h2>
      <Line>
        <strong>
          {studentName}'s (<a href={`mailto:${studentEmail}`}>{studentEmail}</a>)
        </strong>{' '}
        Stripe plan or subscription has been canceled.
      </Line>
      {!isAccessDisabled && (
        <Line>
          They still have access to the course because you explicitly selected to not disable access
          for this subscription.
        </Line>
      )}
      <PrimaryAction href={manageUrl}>View details</PrimaryAction>
    </BasicEmailWrapper>,
  );
}

/**
 * Guide notification email for student payment failure
 */
export function StudentPaymentFailure(
  opts: PaymentEmailParams & { studentName: string; studentEmail: string; payment: Payment },
) {
  return render(
    <BasicEmailWrapper footer={<MarketingFooter />}>
      <h2>{opts.title}</h2>
      <Line>
        <strong>
          {opts.studentName}'s (<a href={`mailto:${opts.studentEmail}`}>{opts.studentEmail}</a>)
        </strong>
        {' payment of '}
        <strong>
          {fmt.price({
            currency: opts.payment.currency,
            priceInCents: opts.payment.amount,
          })}
        </strong>{' '}
        failed on {paymentDate(opts.payment.date, opts.timezone)}.
      </Line>
      <PrimaryAction href={opts.manageUrl}>View details</PrimaryAction>
    </BasicEmailWrapper>,
  );
}
